/**
 * Controllo accessi per ruolo attivo (studente / professore / genitore) e
 * preferenze separate per ciascun profilo.
 *
 * Le funzioni sono pure: le usano sia la UI (gate delle rotte, menu) sia il
 * lato server (server function guard), così il comportamento resta identico.
 */

export type AccountRole = "student" | "teacher" | "parent";

export const ALL_ROLES: AccountRole[] = ["student", "teacher", "parent"];

/** Massimo di profili su uno stesso account (solo per i maggiorenni). */
export const MAX_PROFILES = 2;

export function isRole(v: unknown): v is AccountRole {
  return v === "student" || v === "teacher" || v === "parent";
}

/** Rotte riservate: prefisso → ruoli ammessi. L'ordine conta (più specifico prima). */
const ROUTE_RULES: { prefix: string; roles: AccountRole[] }[] = [
  { prefix: "/teacher-hub", roles: ["teacher"] },
  { prefix: "/teacher-results", roles: ["teacher"] },
  { prefix: "/teaching-studio", roles: ["teacher"] },
  { prefix: "/teacher", roles: ["teacher"] },
  { prefix: "/student/", roles: ["teacher", "parent"] },
  { prefix: "/family", roles: ["parent"] },
  { prefix: "/studio", roles: ["student"] },
  { prefix: "/planner", roles: ["student"] },
  { prefix: "/quiz", roles: ["student"] },
  { prefix: "/flashcards", roles: ["student"] },
  { prefix: "/learn", roles: ["student"] },
  { prefix: "/tools", roles: ["student"] },
  { prefix: "/programs", roles: ["student"] },
  { prefix: "/prep", roles: ["student"] },
  { prefix: "/breaks", roles: ["student"] },
  { prefix: "/rewards", roles: ["student"] },
  { prefix: "/classes", roles: ["student"] },
  { prefix: "/stats", roles: ["student", "parent"] },
];

/** Ruoli ammessi su un percorso (tutti se la rotta è condivisa). */
export function allowedRoles(pathname: string): AccountRole[] {
  const path = pathname.replace(/\/+$/, "") || "/";
  const rule = ROUTE_RULES.find((r) => path === r.prefix || path.startsWith(`${r.prefix}/`));
  return rule ? rule.roles : ALL_ROLES;
}

/** Il ruolo attivo può aprire questo percorso? */
export function canAccess(pathname: string, role: AccountRole): boolean {
  return allowedRoles(pathname).includes(role);
}

/** Home del ruolo: dove rimandare chi non è autorizzato. */
export function homeForRole(role: AccountRole): string {
  return role === "teacher" ? "/teacher" : role === "parent" ? "/family" : "/";
}

/* -------------------------------------------------------------------------- */
/* Profili: normalizzazione, limiti e messaggi chiari                          */
/* -------------------------------------------------------------------------- */

export function normalizeRoleList(roles: unknown, isAdult: boolean, active?: unknown): AccountRole[] {
  const clean: AccountRole[] = [];
  for (const r of Array.isArray(roles) ? roles : []) {
    if (isRole(r) && !clean.includes(r)) clean.push(r);
  }
  if (clean.length === 0) clean.push(isRole(active) ? active : "student");
  return isAdult ? clean.slice(0, MAX_PROFILES) : clean.slice(0, 1);
}

export type AddProfileCheck = { ok: true } | { ok: false; reason: "duplicate" | "minor" | "max" };

/** Verifica se si può aggiungere un profilo (limite 2 e solo maggiorenni). */
export function canAddProfile(roles: AccountRole[], isAdult: boolean, next: AccountRole): AddProfileCheck {
  if (roles.includes(next)) return { ok: false, reason: "duplicate" };
  if (!isAdult) return { ok: false, reason: "minor" };
  if (roles.length >= MAX_PROFILES) return { ok: false, reason: "max" };
  return { ok: true };
}

/** Un profilo può essere rimosso solo se ne resta almeno uno. */
export function canRemoveProfile(roles: AccountRole[]): boolean {
  return roles.length > 1;
}

/* -------------------------------------------------------------------------- */
/* Etichette e preferenze per profilo                                          */
/* -------------------------------------------------------------------------- */

export type RoleLabels = Partial<Record<AccountRole, string>>;

export function normalizeRoleLabels(raw: unknown): RoleLabels {
  const out: RoleLabels = {};
  if (raw && typeof raw === "object") {
    for (const [k, v] of Object.entries(raw as Record<string, unknown>)) {
      if (isRole(k) && typeof v === "string" && v.trim()) out[k] = v.trim().slice(0, 40);
    }
  }
  return out;
}

/** Preferenze salvate separatamente per ogni profilo. */
export type RolePrefs = {
  language?: string;
  dsaMode?: boolean;
  dsaIntensity?: "light" | "standard" | "high";
  focusMode?: boolean;
  studyStyle?: string;
  notif?: Record<string, unknown>;
};

export type RolePrefsMap = Partial<Record<AccountRole, RolePrefs>>;

export function normalizeRolePrefs(raw: unknown): RolePrefsMap {
  const out: RolePrefsMap = {};
  if (!raw || typeof raw !== "object") return out;
  for (const [k, v] of Object.entries(raw as Record<string, unknown>)) {
    if (!isRole(k) || !v || typeof v !== "object") continue;
    const p = v as Record<string, unknown>;
    const prefs: RolePrefs = {};
    if (typeof p["language"] === "string") prefs.language = p["language"];
    if (typeof p["dsaMode"] === "boolean") prefs.dsaMode = p["dsaMode"];
    if (p["dsaIntensity"] === "light" || p["dsaIntensity"] === "standard" || p["dsaIntensity"] === "high") {
      prefs.dsaIntensity = p["dsaIntensity"];
    }
    if (typeof p["focusMode"] === "boolean") prefs.focusMode = p["focusMode"];
    if (typeof p["studyStyle"] === "string") prefs.studyStyle = p["studyStyle"];
    if (p["notif"] && typeof p["notif"] === "object") prefs.notif = p["notif"] as Record<string, unknown>;
    out[k] = prefs;
  }
  return out;
}

/** Unisce le preferenze del profilo attivo sopra quelle generali dell'account. */
export function mergeRolePrefs(map: RolePrefsMap, role: AccountRole, base: RolePrefs = {}): RolePrefs {
  return { ...base, ...(map[role] ?? {}) };
}

/** Aggiorna le preferenze di un solo profilo senza toccare gli altri. */
export function setRolePrefs(map: RolePrefsMap, role: AccountRole, patch: RolePrefs): RolePrefsMap {
  return { ...map, [role]: { ...(map[role] ?? {}), ...patch } };
}

/** Rimuove profilo, etichetta e preferenze collegate in un colpo solo. */
export function removeProfile(
  state: { roles: AccountRole[]; labels: RoleLabels; prefs: RolePrefsMap; active: AccountRole; defaultRole: AccountRole },
  role: AccountRole,
): { roles: AccountRole[]; labels: RoleLabels; prefs: RolePrefsMap; active: AccountRole; defaultRole: AccountRole } {
  if (!canRemoveProfile(state.roles) || !state.roles.includes(role)) return state;
  const roles = state.roles.filter((r) => r !== role);
  const labels = { ...state.labels };
  delete labels[role];
  const prefs = { ...state.prefs };
  delete prefs[role];
  const active = roles.includes(state.active) ? state.active : roles[0]!;
  const defaultRole = roles.includes(state.defaultRole) ? state.defaultRole : active;
  return { roles, labels, prefs, active, defaultRole };
}

/* -------------------------------------------------------------------------- */
/* Preferenze ↔ colonne del profilo                                            */
/* -------------------------------------------------------------------------- */

/**
 * Le impostazioni (lingua, accessibilità, stile di studio, notifiche) vivono
 * nelle colonne del profilo. Queste due funzioni le "fotografano" nel profilo
 * attivo e le riapplicano quando si passa a un altro profilo, così ogni ruolo
 * mantiene le proprie senza interferire con l'altro.
 */
export function prefsFromProfileRow(row: Record<string, unknown> | null | undefined): RolePrefs {
  const r = row ?? {};
  const prefs: RolePrefs = {};
  if (typeof r["language"] === "string") prefs.language = r["language"];
  if (typeof r["dsa_mode_enabled"] === "boolean") prefs.dsaMode = r["dsa_mode_enabled"];
  const intensity = r["dsa_intensity"];
  if (intensity === "light" || intensity === "standard" || intensity === "high") prefs.dsaIntensity = intensity;
  if (typeof r["focus_mode_enabled"] === "boolean") prefs.focusMode = r["focus_mode_enabled"];
  if (typeof r["learning_style"] === "string") prefs.studyStyle = r["learning_style"];
  if (r["notif_prefs"] && typeof r["notif_prefs"] === "object") {
    prefs.notif = r["notif_prefs"] as Record<string, unknown>;
  }
  return prefs;
}

export function profilePatchFromPrefs(prefs: RolePrefs): Record<string, unknown> {
  const patch: Record<string, unknown> = {};
  if (prefs.language) patch["language"] = prefs.language;
  if (typeof prefs.dsaMode === "boolean") patch["dsa_mode_enabled"] = prefs.dsaMode;
  if (prefs.dsaIntensity) patch["dsa_intensity"] = prefs.dsaIntensity;
  if (typeof prefs.focusMode === "boolean") patch["focus_mode_enabled"] = prefs.focusMode;
  if (prefs.studyStyle) patch["learning_style"] = prefs.studyStyle;
  if (prefs.notif) patch["notif_prefs"] = prefs.notif;
  return patch;
}
